import React, { useEffect, useMemo, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Upload } from 'lucide-react';
import { toast } from 'sonner';

import { v1UploadForm } from '@/lib/api';
import {
  splitEmails,
  isValidEmail,
  panelOverlapsCandidate,
  toDatetimeLocalValue,
  minutesToDurationSec,
  clampDurationSec,
  readVideoDurationSec,
} from '@/lib/panelInterviewForm';
import { fmtMMSS } from '@/lib/format';
import { CANDIDATE_ON_PANEL_MSG } from '@/components/panelInterviews/panelInterviewConstants';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';

export function ManualUploadDialog({
  open, onOpenChange, candidateId, candidateEmail, evaluationId,
  vacantRounds = [], panelKind = 'tech',
}) {
  const qc = useQueryClient();
  const kind = panelKind === 'hr' ? 'hr' : 'tech';
  const isHr = kind === 'hr';

  const [subject, setSubject] = useState(
    isHr ? 'HR panel call' : 'Technical panel interview',
  );
  const [description, setDescription] = useState(
    isHr
      ? 'External HR call recording uploaded manually.'
      : 'External interview recording uploaded manually.',
  );
  const [panelEmailsRaw, setPanelEmailsRaw] = useState('');
  const [recordingFile, setRecordingFile] = useState(null);
  const [transcriptFile, setTranscriptFile] = useState(null);
  const [pipelineStageId, setPipelineStageId] = useState('');
  const [startAt, setStartAt] = useState(() => toDatetimeLocalValue(new Date()));
  const [durationSec, setDurationSec] = useState(null);
  const [durationFallbackMin, setDurationFallbackMin] = useState('');
  const [durationFromVideo, setDurationFromVideo] = useState(false);
  const [durationLoading, setDurationLoading] = useState(false);

  useEffect(() => {
    if (!open || isHr) return;
    if (vacantRounds.length) {
      setPipelineStageId(vacantRounds[0].id);
      if (vacantRounds[0].label) setSubject(vacantRounds[0].label);
    } else {
      setPipelineStageId('');
    }
  }, [open, vacantRounds, isHr]);

  useEffect(() => {
    if (!open) return;
    setSubject(isHr ? 'HR panel call' : 'Technical panel interview');
    setDescription(
      isHr
        ? 'External HR call recording uploaded manually.'
        : 'External interview recording uploaded manually.',
    );
    setStartAt(toDatetimeLocalValue(new Date()));
    setDurationSec(null);
    setDurationFallbackMin('');
    setDurationFromVideo(false);
    setDurationLoading(false);
  }, [open, isHr]);

  useEffect(() => {
    if (!recordingFile) {
      setDurationSec(null);
      setDurationFromVideo(false);
      setDurationLoading(false);
      return undefined;
    }
    let cancelled = false;
    setDurationLoading(true);
    setDurationFromVideo(false);
    setDurationSec(null);
    readVideoDurationSec(recordingFile)
      .then((sec) => {
        if (cancelled) return;
        setDurationSec(sec);
        setDurationFromVideo(true);
        setDurationFallbackMin('');
      })
      .catch(() => {
        if (cancelled) return;
        setDurationFromVideo(false);
        setDurationSec(null);
      })
      .finally(() => {
        if (!cancelled) setDurationLoading(false);
      });
    return () => { cancelled = true; };
  }, [recordingFile]);

  const panelEmails = useMemo(() => splitEmails(panelEmailsRaw), [panelEmailsRaw]);
  const invalidEmails = panelEmails.filter((e) => !isValidEmail(e));
  const candidateOnPanel = panelOverlapsCandidate(panelEmails, candidateEmail);
  const effectiveDurationSec = durationFromVideo
    ? clampDurationSec(durationSec)
    : minutesToDurationSec(durationFallbackMin);
  const startDate = startAt ? new Date(startAt) : null;
  const startValid = !!startDate && !Number.isNaN(startDate.getTime());
  const canSubmit =
    !!evaluationId &&
    subject.trim() &&
    candidateEmail &&
    panelEmails.length > 0 &&
    invalidEmails.length === 0 &&
    !candidateOnPanel &&
    !!recordingFile &&
    startValid &&
    effectiveDurationSec != null &&
    !durationLoading &&
    (isHr || !!pipelineStageId);

  const mutation = useMutation({
    mutationFn: async () => {
      const form = new FormData();
      form.append('candidate_id', candidateId);
      form.append('evaluation_id', evaluationId);
      form.append('subject', subject.trim());
      form.append('description', description.trim() || '');
      form.append('timezone_label', Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC');
      form.append('candidate_email', candidateEmail);
      form.append('panel_emails', panelEmails.join(', '));
      form.append('panel_kind', kind);
      if (!isHr && pipelineStageId) {
        form.append('pipeline_stage_id', pipelineStageId);
      }
      const start = new Date(startAt);
      const end = new Date(start.getTime() + effectiveDurationSec * 1000);
      form.append('start_at', start.toISOString());
      form.append('end_at', end.toISOString());
      form.append('recording', recordingFile);
      if (transcriptFile) {
        form.append('transcript', transcriptFile);
      }
      return v1UploadForm('/panels/manual-upload', form);
    },
    onSuccess: () => {
      toast.success(
        isHr
          ? (transcriptFile
            ? 'Manual upload saved — recording and transcript are ready.'
            : 'Manual upload saved — transcript will be generated from the recording.')
          : (transcriptFile
            ? 'Manual upload saved — AI evaluation queued.'
            : 'Manual upload saved — transcript will be generated, then AI evaluation queued.'),
      );
      qc.invalidateQueries({ queryKey: ['v1-panels', candidateId, kind] });
      // PipelineTimeline reads panels from candidate hydrate, not v1-panels.
      qc.invalidateQueries({ queryKey: ['v1-candidate', candidateId] });
      qc.invalidateQueries({ queryKey: ['v1-candidate-activity', candidateId] });
      onOpenChange(false);
      setRecordingFile(null);
      setTranscriptFile(null);
      setPanelEmailsRaw('');
    },
    onError: (err) => {
      const detail = err?.response?.data?.detail;
      const msg =
        (detail && typeof detail === 'object' && detail.message) ||
        (typeof detail === 'string' ? detail : null) ||
        'Could not upload panel media.';
      toast.error(msg);
    },
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl" data-testid="manual-upload-panel-dialog">
        <DialogHeader>
          <DialogTitle>Manual Upload</DialogTitle>
          <DialogDescription>
            {isHr
              ? 'Upload a recording from an external Teams, Zoom, or other HR call. Transcript is optional — if omitted, HireIQ transcribes the recording. The same Recording and Transcript experience as a scheduled HR call is shown.'
              : 'Upload a recording from an external Teams, Zoom, or other interview. Transcript is optional — if omitted, HireIQ automatically transcribes the recording, then runs the same evaluation as a scheduled panel.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-3">
          {!isHr && (
            vacantRounds.length > 1 ? (
              <div>
                <label className="text-xs font-medium text-muted-foreground">Pipeline round</label>
                <Select
                  value={pipelineStageId}
                  onValueChange={(v) => {
                    setPipelineStageId(v);
                    const round = vacantRounds.find((r) => r.id === v);
                    if (round?.label) setSubject(round.label);
                  }}
                >
                  <SelectTrigger data-testid="manual-panel-round-select">
                    <SelectValue placeholder={vacantRounds.length ? 'Select round' : 'No vacant rounds'} />
                  </SelectTrigger>
                  <SelectContent>
                    {vacantRounds.map((r) => (
                      <SelectItem key={r.id} value={r.id}>{r.label || r.id}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            ) : (
              <p className="text-[11px] text-muted-foreground" data-testid="manual-panel-round-fixed">
                Round: {vacantRounds[0]?.label || 'Panel interview'}
              </p>
            )
          )}
          <div>
            <label className="text-xs font-medium text-muted-foreground">Subject</label>
            <Input value={subject} onChange={(e) => setSubject(e.target.value)} data-testid="manual-panel-subject-input" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Notes (optional)</label>
            <Textarea value={description} onChange={(e) => setDescription(e.target.value)} rows={2} data-testid="manual-panel-description-input" />
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Interview start</label>
              <Input
                type="datetime-local"
                value={startAt}
                onChange={(e) => setStartAt(e.target.value)}
                data-testid="manual-panel-start-input"
              />
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">Duration</label>
              {durationLoading && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="manual-panel-duration-loading">
                  Reading video duration…
                </p>
              )}
              {!durationLoading && durationFromVideo && effectiveDurationSec != null && (
                <p className="mt-2 text-xs text-muted-foreground" data-testid="manual-panel-duration-from-video">
                  {fmtMMSS(effectiveDurationSec)} (from video)
                </p>
              )}
              {!durationLoading && !durationFromVideo && (
                <Input
                  type="number"
                  min="1"
                  step="0.1"
                  value={durationFallbackMin}
                  onChange={(e) => setDurationFallbackMin(e.target.value)}
                  placeholder="Minutes"
                  data-testid="manual-panel-duration-minutes-input"
                />
              )}
              {startValid && effectiveDurationSec != null && (
                <p className="mt-1 text-[11px] text-muted-foreground" data-testid="manual-panel-end-preview">
                  Ends {toDatetimeLocalValue(new Date(new Date(startAt).getTime() + effectiveDurationSec * 1000)).replace('T', ' ')}
                </p>
              )}
            </div>
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">Candidate email</label>
            <Input value={candidateEmail || ''} disabled data-testid="manual-panel-candidate-email" />
          </div>
          <div>
            <label className="text-xs font-medium text-muted-foreground">
              {isHr ? 'HR panellists' : 'Panel members'}{' '}
              <span className="text-muted-foreground">(comma or newline separated)</span>
            </label>
            <Textarea
              rows={2}
              value={panelEmailsRaw}
              onChange={(e) => setPanelEmailsRaw(e.target.value)}
              placeholder="hr@kadellabs.com, manager@kadellabs.com"
              data-testid="manual-panel-emails-input"
            />
            {invalidEmails.length > 0 && (
              <p className="mt-1 text-xs text-destructive">Invalid email(s): {invalidEmails.join(', ')}</p>
            )}
            {candidateOnPanel && (
              <p className="mt-1 text-xs text-destructive" data-testid="manual-panel-candidate-on-panel-error">
                {CANDIDATE_ON_PANEL_MSG}
              </p>
            )}
          </div>
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            <div>
              <label className="text-xs font-medium text-muted-foreground">Interview recording / video</label>
              <Input
                type="file"
                accept="video/*,.mp4,.webm,.mov,.avi,.mkv"
                onChange={(e) => setRecordingFile(e.target.files?.[0] || null)}
                data-testid="manual-panel-recording-input"
              />
              {recordingFile && (
                <p className="mt-1 text-xs text-muted-foreground truncate">{recordingFile.name}</p>
              )}
            </div>
            <div>
              <label className="text-xs font-medium text-muted-foreground">
                Interview transcript (optional)
              </label>
              <Input
                type="file"
                accept=".txt,.vtt,.srt,text/plain,text/vtt"
                onChange={(e) => setTranscriptFile(e.target.files?.[0] || null)}
                data-testid="manual-panel-transcript-input"
              />
              {transcriptFile && (
                <p className="mt-1 text-xs text-muted-foreground truncate">{transcriptFile.name}</p>
              )}
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} data-testid="manual-panel-cancel-button">
            Cancel
          </Button>
          <Button
            onClick={() => mutation.mutate()}
            disabled={!canSubmit || mutation.isPending}
            data-testid="manual-panel-upload-submit"
            className="gap-1.5"
          >
            <Upload className="h-4 w-4" />
            {mutation.isPending
              ? 'Uploading…'
              : isHr
                ? 'Upload'
                : 'Upload & Evaluate'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
